import {DataStatus} from 'platform/components';
import {Box, Grid, HStack, Text, VStack} from 'platform/foundation';

import {RentalOrderResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {SkeletonLoadingBox} from '@dms/shared';

import {DOT_CHARACTER} from 'shared/web';

import {KanbanFilters} from './components/KanbanFilters';
import {RentalOrderKanbanCard} from './components/RentalOrderKanbanCard';
import {useKanbanControl} from './hooks/useKanbanControl';
import {getCardsForColumn} from './utils/getCardsForColumn';

const KANBAN_COLUMNS: {label: string; value: RentalOrderResponseBody['state']}[] = [
  {label: i18n.t('page.rentalOrder.labels.draft'), value: 'draft'},
  {label: i18n.t('page.rentalOrder.labels.reserved'), value: 'reserved'},
  {label: i18n.t('page.rentalOrder.labels.rented'), value: 'rented'},
  {label: i18n.t('page.rentalOrder.labels.returned'), value: 'returned'},
  {label: i18n.t('page.rentalOrder.labels.closed'), value: 'closed'},
  {label: i18n.t('page.rentalOrder.labels.cancelled'), value: 'canceled'},
];

export function RentalOrderKanban() {
  const {rentalOrders, isLoading, isError, filters} = useKanbanControl();

  return (
    <DataStatus
      isError={isError}
      isEmpty={!isLoading && !rentalOrders?.rows?.length}
      minHeight={200}
    >
      <Box width="100%" height="100%" overflow="auto" paddingBottom={[2, 4]}>
        <KanbanFilters {...filters} />

        <Grid columns={KANBAN_COLUMNS.length} spacing={2}>
          {KANBAN_COLUMNS.map((column) => {
            const cards = getCardsForColumn(column.value, rentalOrders?.rows ?? []);

            return (
              <Box
                borderRadius="small"
                minWidth={[65, 55]}
                width="100%"
                height="100%"
                backgroundColor="palettes.neutral.30.100"
                padding={2}
                key={`business-case-kanban-${column.value}`}
              >
                <VStack width="100%" spacing={3}>
                  <HStack spacing={1} align="center">
                    <Text size="small" alternative color="primary">
                      {i18n.t(column.label)}
                    </Text>
                    <Text size="small" color="tertiary">
                      {DOT_CHARACTER}
                    </Text>
                    <SkeletonLoadingBox isLoading={isLoading} height={5} width={10}>
                      <HStack spacing={1} align="center">
                        <Text size="small" color="tertiary">
                          {cards.length || 0}
                        </Text>
                      </HStack>
                    </SkeletonLoadingBox>
                  </HStack>

                  <SkeletonLoadingBox isLoading={isLoading} height={38}>
                    <VStack width="100%" spacing={3}>
                      {cards.map((card) => (
                        <RentalOrderKanbanCard key={card.id} card={card} />
                      ))}
                    </VStack>
                  </SkeletonLoadingBox>
                </VStack>
              </Box>
            );
          })}
        </Grid>
      </Box>
    </DataStatus>
  );
}
